import React, { useEffect, useState } from 'react';
import { Button, Flex, Stack, Transition, Box, Container, Title } from '@mantine/core';
import { useTimeout } from '@mantine/hooks';
import games from './games';
import DemoCard from './DemoCard';
import { CardBackground } from '../HomePageBackground';
import { chunk, slice } from '../../utils';

const MIN_SWIPE_DISTANCE = 50;
const TRANSITION_DURATION = 400;
const DEMO_CYCLE_DURATION = 5000;

function HomePageDemo() {
  
  let selectNextGame;
  
  const [selectedGameId, setSelectedGameId] = useState(games[0].id);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touched, setTouched] = useState(false);
  const [selectedGame, setSelectedGame] = useState(games[0]);
  const [isCardMounted, setIsCardMounted] = useState(false);
  const [transitionStyle, setTransitionStyle] = useState('rotate-left');
  const [tracks, setTracks] = useState([]);
  const { start, clear } = useTimeout(() => selectNextGame(), DEMO_CYCLE_DURATION);

  const selectGame = (gameId, isTouched) => {
    if (isTouched) {
      clear();
      setTouched(true);
    }
    setSelectedGameId(gameId);
    setIsCardMounted(false);
    setTimeout(() => {
      const newGame = games.find((game) => game.id === gameId);
      setSelectedGame(newGame);
    }, TRANSITION_DURATION);
  }

  const getCurrentGame = () => {
    const currentGameIdx = games.findIndex((game) => game.id === selectedGameId);
    return currentGameIdx;
  }

  selectNextGame = (isTouched = false) => {
    const currentGameIdx = getCurrentGame();
    const nextGame = (currentGameIdx + 1 === games.length) ? 0 : (currentGameIdx + 1);
    selectGame(games[nextGame].id, isTouched);
    if (!isTouched) {
      start();
    }
  }

  const selectPreviousGame = () => {
    const currentGameIdx = getCurrentGame();
    const prevGame = (currentGameIdx - 1 === -1) ? (games.length - 1) : (currentGameIdx - 1);
    selectGame(games[prevGame].id, true);
  }

  const formatTracks = (tracksArr) => {
    let newTracks = slice(tracksArr);
    newTracks = chunk(newTracks);
    setTracks(newTracks);
  };

  useEffect(() => {
    const startOrPauseDemo = touched ? clear : start;
    startOrPauseDemo();
    formatTracks(selectedGame.tracks);
    setIsCardMounted(true);
    setTransitionStyle( transitionStyle === 'rotate-left' ? 'rotate-right' : 'rotate-left' );
  }, [selectedGame]);

  const onTouchStart = (e) => {
    setTouchEnd(null) // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;
    if (isLeftSwipe) {
      setTransitionStyle( 'rotate-left');
      selectNextGame(true);
    }
    if (isRightSwipe) {
      setTransitionStyle( 'rotate-right');
      selectPreviousGame();
    }
  }

  return (
    <Box style={{ overflow: 'hidden' }}>
      <Container p='xl' pt={{ base: 0, md: 40 }} pos='relative' style={{ zIndex: 2 }} >
        <Flex direction={{ base: 'column', sm: 'row' }} align={{ sm: 'center' }} gap={{ base: 0, sm: 80 }}>
          
          <Stack mb={32} w='100%'>
            <Title size='h3' order={2}>
              Themes for any occasion
            </Title>

            <Flex gap='xs' wrap='wrap'>
              { games.map((game) => (
                <Button
                  key={game.id}
                  variant={ game.id === selectedGameId ? 'filled' : 'light' }
                  color='violet'
                  radius='xl'
                  size='md'
                  onClick={() => (game.id !== selectedGameId) && selectGame(game.id, true)}
                >
                  {game.label}
                </Button>
              ))}
            </Flex>
          </Stack>
          
          <Box w='100%'>
            <Transition w='100%' mounted={isCardMounted} transition={transitionStyle} duration={TRANSITION_DURATION} timingFunction='ease'>
              {(styles) => 
                <DemoCard
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  game={selectedGame}
                  rows={tracks}
                  transitionStyles={styles}
                  tracks={tracks}
                />
              }
            </Transition>
          </Box>
        </Flex>
      </Container>

      <CardBackground />

    </Box>
  );
}

export default HomePageDemo;